import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, catchError, EMPTY, Observable, Subject, takeUntil} from "rxjs";
import {UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {BillingCounterpartyModel} from "@services/back-office/model/billingCounterpartyModel";
import {filter, map, take} from "rxjs/operators";
import {DictionariesService} from "@shared/services/dictionaries.service";
import {select, Store} from "@ngrx/store";
import {Reducers} from "@app/reducers";
import * as counterpartyActions from '@billing/ngrx/actions/counterparty.actions';
import * as contractActions from '@billing/ngrx/actions/contracts.actions';
import * as contactActions from '@billing/ngrx/actions/contracts.actions';
import {BillingService} from "@shared/services/billing.service";
import { HttpErrorResponse } from "@angular/common/http";
import {SharedService} from "@shared/shared.service";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {CompanyClassifierModel} from "@services/accounting/model/companyClassifierModel";
import {BillingContractModel} from "@services/back-office/model/billingContractModel";
import {ListItemType} from "@shared/enum/list-item-type";
import {ConfirmationDialogComponent} from "@shared/components/confirmation-dialog/confirmation-dialog.component";
import {ConfigService} from "@config/config.service";
import {Actions, ofType} from "@ngrx/effects";
import {PermissionsModel} from "@shared/models/permission.model";
import * as selectFromLogin from "@login/ngrx/login.selectors";
import {ValidateIfObject} from "@shared/validators/if-object.validator";
import {GovernmentModel} from "@services/back-office/model/governmentModel";
import {MatTableDataSource} from '@angular/material/table';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CounterpartyViewDialogComponent} from '@billing/components/counterparty/counterparty-view-dialog/counterparty-view-dialog.component';

@Component({
    selector: 'app-counterparty-dialog',
    templateUrl: './counterparty-dialog.component.html',
    styleUrls: ['./counterparty-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CounterpartyDialogComponent implements OnInit, OnDestroy {
    loading$ = new BehaviorSubject(false);
    /** Form */
    companyForm: UntypedFormGroup;
    contractsDataSource: MatTableDataSource<BillingContractModel> = new MatTableDataSource<BillingContractModel>();
    columns: string[] = ['name', 'amount', 'actions'];
    contractsTabLoaded = false;
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;
    roleDataSource: MatTableDataSource<GovernmentModel> = new MatTableDataSource<GovernmentModel>();
    roleColumns: string[] = ['user', 'role', 'actions'];
    private destroyed$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            counterparty: BillingCounterpartyModel,
            disableAddContract?: boolean
        },
        public dialogRef: MatDialogRef<CounterpartyDialogComponent>,
        private formBuilder: UntypedFormBuilder,
        private dictionariesService: DictionariesService,
        private store$: Store<Reducers>,
        private billingService: BillingService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private changeDetectorRef: ChangeDetectorRef,
        private sharedService: SharedService,
        private config: ConfigService,
        private actions$: Actions
    ) {
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
    }

    ngOnInit(): void {
        this.initForm();

        /** Patch Form On Edit */
        if (this.data.counterparty) {
            this.patchForm(this.data.counterparty);
        }

        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
                this.permissions = permissions;
            }
        );

        this.watchContractActions();
        this.watchCounterpartyActionsActions();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    checkPermissions(permission: string): boolean {
        return this.permissions ?
            SharedService.checkPermission(permission, this.permissions[0].permissions, this.permissions[0].permissionsExists) :
            false;
    }

    initForm() {
        this.companyForm = this.formBuilder.group({
            type: ['LEGAL', [Validators.required]],
            code: ['', [Validators.required]],
            name: ['', [Validators.required]],
            address: ['', [Validators.required]],
            director: ['', [Validators.required]],
            directorPosition: ['', [Validators.required]],
            directorPositionPersonal: ['', [Validators.required]],
            manager: ['', [Validators.required, ValidateIfObject]],
        });
    }

    patchForm(company: BillingCounterpartyModel) {
        if (!this.companyForm) {
            this.initForm();
        }

        this.companyForm.patchValue({
            ...company && company.type && {type: company.type},
            ...company && company.code && {code: company.code},
            ...company && company.name && {name: company.name},
            ...company && company.address && {address: company.address},
            ...company && company.director && {director: company.director},
            ...company && company.directorPosition && {directorPosition: company.directorPosition},
            ...company && company.directorPositionPersonal && {directorPositionPersonal: company.directorPositionPersonal},
            ...company && company.manager && {manager: company.manager}
        });
    }

    fillByCode() {
        const code: string = this.companyForm.get('code')?.value;
        if (!code?.length) {
            return;
        }

        this.dictionariesService.searchByCode(code).pipe(
            takeUntil(this.destroyed$),
            map((res: CompanyClassifierModel) => {
                if (res) {
                    this.patchForm({
                        code: res.code,
                        name: res.name,
                        address: res.address,
                        director: res.director,
                    });
                }
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            }),
        ).subscribe();
    }

    /** Add or Edit Company */
    submitForm() {
        this.loading$.next(true);
        const formData = this.companyForm.getRawValue();
        const request: BillingCounterpartyModel = {
            ...formData.address && {address: formData.address},
            ...formData.code && {code: formData.code},
            ...formData.director && {director: formData.director},
            ...formData.directorPosition && {directorPosition: formData.directorPosition},
            ...formData.directorPositionPersonal && {directorPositionPersonal: formData.directorPositionPersonal},
            ...formData.name && {name: formData.name},
            ...formData.type && {type: formData.type},
            ...formData.manager && {manager: formData.manager},
        };

        if (this.data?.counterparty?.id) {
            this.editCounterparty({...request, id: this.data.counterparty.id});
        } else {
            this.addCounterparty(request);
        }
    }

    addCounterparty(counterparty: BillingCounterpartyModel) {
        this.billingService.addCounterparty(counterparty).pipe(
            takeUntil(this.destroyed$),
            map((res: BillingCounterpartyModel) => {
                this.store$.dispatch(new contractActions.AddEditCounterPartySuccess(res));
                this.store$.dispatch(new counterpartyActions.AddCounterpartiesSuccess(res));
                this.loading$.next(false);
                this.closeDialog(true);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    editCounterparty(counterparty: BillingCounterpartyModel) {
        this.billingService.editCounterparty(counterparty).pipe(
            takeUntil(this.destroyed$),
            map((res: BillingCounterpartyModel) => {
                this.store$.dispatch(new contractActions.AddEditCounterPartySuccess(res));
                this.store$.dispatch(new counterpartyActions.EditCounterpartiesSuccess(res));
                this.loading$.next(false);
                this.closeDialog(true);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    tabChanged(event: any) {
        if (event.index == 1 && !this.contractsTabLoaded) {
            this.getContracts();
        }
    }

    getContracts() {
        if (!this.checkPermissions('BILLING_NEW_READ') && !this.checkPermissions('BILLING_SINGLE_CONTRACT_EDIT')) {
            return;
        }

        this.billingService.getCounterpartyContracts(this.data.counterparty.id!).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: BillingContractModel[]) => {
                this.contractsDataSource.data = result;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.contractsTabLoaded = true;
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    addContract(type: string) {
        this.sharedService.createCrmItem(ListItemType.CONTRACT, {counterparty: this.data.counterparty, type: type}, true);
    }

    editContract(item: BillingContractModel) {
        this.sharedService.openCrmItem(ListItemType.CONTRACT, item.id!);
    }

    deleteContract(item: BillingContractModel) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.billing.agreements.actions.confirmDeleteAgreement',
                    {number: item.number ? item.number : ''}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe().subscribe((result) => {
            if (result) {
                this.store$.dispatch(new contactActions.DeleteContracts(item.id!));
            }
        });
    }

    // addCounterpartyUser() {
    //     const counterpartyRoleDialog$ = this.sharedService.openDialog(
    //         CounterpartyRoleDialogComponent,
    //         {...this.config.addItemDialog, maxWidth: '400px', hasBackdrop: true},
    //         {
    //             counterparty: this.data.counterparty
    //         }
    //     );
    //
    //     counterpartyRoleDialog$.afterClosed().pipe(
    //         take(1),
    //     ).subscribe((dialogData: { result: boolean, data?: any }) => {
    //         if (dialogData.result) {
    //             const users = this.roleDataSource.data;
    //             users.push(dialogData.data);
    //             this.roleDataSource.data = users;
    //         }
    //     });
    // }

    // deleteCounterpartyUser(element: any) {
    //     const dialog$ = this.sharedService.openDialog(
    //         ConfirmationDialogComponent,
    //         {...this.config.confirmationDialog, hasBackdrop: true},
    //         {
    //             title: SharedService.getTranslation(
    //                 this.translateService,
    //                 'modules.clients.associations.associationViewDialog.deleteRoleWarning',
    //                 {name: element.user?.userName?.fullName}
    //             ),
    //             cancelText: 'common.buttons.cancel',
    //             submitText: 'common.buttons.delete',
    //         }
    //     );
    //
    //     dialog$.afterClosed().pipe(
    //         take(1)
    //     ).subscribe((result) => {
    //         if (result) {
    //             this.billingService.deleteCounterpartyUser(element.id!).pipe(
    //                 take(1),
    //                 map(() => {
    //                     const positions = this.roleDataSource.data;
    //                     if (positions && positions.length) {
    //                         const positionIndex = positions.findIndex((item: GovernmentModel) => item.user?.userId === element.user?.userId);
    //
    //                         if (positionIndex !== -1) {
    //                             positions.splice(positionIndex, 1);
    //                             this.roleDataSource.data = positions;
    //                             this.changeDetectorRef.detectChanges();
    //                         }
    //                     }
    //                 })
    //             ).subscribe();
    //         }
    //     });
    // }

    watchContractActions() {
        this.actions$.pipe(
            ofType(contractActions.ContractsActionTypes.AddContractsSuccess,
                contractActions.ContractsActionTypes.EditContractsSuccess,
                contractActions.ContractsActionTypes.DeleteContractsSuccess),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.getContracts();
        });
    }

    watchCounterpartyActionsActions() {
        this.actions$.pipe(
            ofType(counterpartyActions.CounterpartiesActionTypes.AddCounterpartiesSuccess),
            take(1),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe((action: counterpartyActions.AddCounterpartiesSuccess) => {
            this.loading$.next(false);
            if (action.payload.id) {
                this.sharedService.openDialog(
                    CounterpartyViewDialogComponent,
                    {...this.config.viewItemDialog, maxWidth: '1100px', ...this.config.canMinimizeDialog},
                    {
                        counterparty: action.payload
                    }
                );
            }
            this.closeDialog(true);
        });

        this.actions$.pipe(
            ofType(counterpartyActions.CounterpartiesActionTypes.EditCounterpartiesSuccess),
            take(1),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe(() => {
            this.loading$.next(false);
            this.closeDialog(true);
        });

        this.actions$.pipe(
            ofType(counterpartyActions.CounterpartiesActionTypes.AddCounterpartiesFailure,
                counterpartyActions.CounterpartiesActionTypes.EditCounterpartiesFailure),
            take(1),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
